import moment from "moment";

// Names for downloaded media files and zips. Staff find files by DN and by
// the day of treatment, and remarks are usually Thai, so both stay in the
// name: 20260912_IntraOral_DN0012_ก่อนจัดฟัน.jpg

const CATEGORY_SLUGS = {
  IntraOralPhoto: "IntraOral",
  ExtraOralPhoto: "ExtraOral",
  XRay: "XRay",
  OpdCard: "OpdCard",
  Document: "Document",
  Template: "Template",
};

const EXTENSIONS = {
  "application/pdf": ".pdf",
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "application/dicom": ".dcm",
};

// Long remarks would make names unwieldy; the start is what identifies them.
const MAX_PART_LENGTH = 60;

/**
 * Makes text safe inside a file name on Windows, macOS and in zips, keeping
 * Thai and every other script: only path and reserved characters go.
 */
export function safeNamePart(text) {
  return Array.from(
    String(text || "")
      // eslint-disable-next-line no-control-regex
      .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, " ")
      .trim()
      .replace(/\s+/g, "-")
  )
    .slice(0, MAX_PART_LENGTH)
    .join("")
    .replace(/^[-.]+|[-.]+$/g, "");
}

export function extensionFor(fileType) {
  return EXTENSIONS[(fileType || "").toLowerCase()] || "";
}

const ymd = date => {
  const m = moment(date);
  return m.isValid() ? m.format("YYYYMMDD") : moment().format("YYYYMMDD");
};

/**
 * @param {Object} item - formatted media file (category, remark, fileType, creationDt)
 * @param {Object} [options]
 * @param {string} [options.dn] - the patient's DN; left out when there is none (clinic forms)
 * @param {string|Date} [options.treatmentDate] - used over the upload date when the file belongs to a treatment
 */
export function buildDownloadName(item, { dn, treatmentDate } = {}) {
  const parts = [
    ymd(treatmentDate || item.creationDt),
    CATEGORY_SLUGS[item.category] || safeNamePart(item.category) || "file",
  ];
  const dnPart = safeNamePart(dn);
  if (dnPart) parts.push(`DN${dnPart}`);
  const remark = safeNamePart(item.remark);
  if (remark) parts.push(remark);
  return `${parts.join("_")}${extensionFor(item.fileType)}`;
}

/**
 * "DN0012_รูป_20260928" (no extension)
 *
 * @param {Object} options
 * @param {string} [options.dn]
 * @param {string} options.label - what the zip holds, e.g. "รูป" or "เอกสาร"
 * @param {string|Date} [options.date] - defaults to today
 */
export function buildZipName({ dn, label, date } = {}) {
  const parts = [];
  const dnPart = safeNamePart(dn);
  if (dnPart) parts.push(`DN${dnPart}`);
  const labelPart = safeNamePart(label);
  if (labelPart) parts.push(labelPart);
  parts.push(date ? ymd(date) : moment().format("YYYYMMDD"));
  return parts.join("_");
}
